import { QueryClientProvider } from "@tanstack/react-query";
import { useNavigationContainerRef } from "expo-router";
import React, { useEffect, useState } from "react";
import { RawIntlProvider } from "react-intl";
import { StatusBar } from "react-native";
import { gestureHandlerRootHOC } from "react-native-gesture-handler";
import { MenuProvider } from "react-native-popup-menu";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { ModalProvider } from "~/components/ModalProvider";
import { AlertProvider } from "./components/AlertProvider/AlertProvider";
import { WithSplashScreen } from "./components/Splash";
import { ErrorAlertProvider } from "./features/error-alert";
import { ToastProvider } from "./features/toast";
import { initialize } from "./initialize";
import { routingInstrumentation } from "./utils/routingInstrumentation";

export const AppEntry = gestureHandlerRootHOC(function AppEntry({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isReady, setIsReady] = useState<
    | { isReady: false }
    | { isReady: true; context: Awaited<ReturnType<typeof initialize>> }
  >({ isReady: false });

  useEffect(() => {
    initialize().then((ctx) => {
      setIsReady({ isReady: true, context: ctx });
    });
  }, []);

  // Capture the NavigationContainer ref and register it with the instrumentation.
  const ref = useNavigationContainerRef();

  useEffect(() => {
    if (ref) {
      routingInstrumentation.registerNavigationContainer(ref);
    }
  }, [ref]);

  return (
    <WithSplashScreen isAppReady={isReady.isReady}>
      <StatusBar
        barStyle="dark-content"
        translucent
        backgroundColor="transparent"
        animated
      />
      {isReady.isReady && (
        <QueryClientProvider client={isReady.context.queryClient}>
          <RawIntlProvider value={intl}>
            <SafeAreaProvider>
              <ModalProvider>
                <AlertProvider>
                  <ErrorAlertProvider>
                    <ToastProvider>
                      <MenuProvider>{children}</MenuProvider>
                    </ToastProvider>
                  </ErrorAlertProvider>
                </AlertProvider>
              </ModalProvider>
            </SafeAreaProvider>
          </RawIntlProvider>
        </QueryClientProvider>
      )}
    </WithSplashScreen>
  );
});
